// src/services/optimizedDataService.ts
// Optimized service untuk mengambil data dengan caching dan tanpa delay yang tidak perlu

import { useCachedData } from '../hooks/useDataCache';
import { ProfileData } from '../types';
import { supabase } from '../lib/supabase';
import type { Certificate } from '../lib/supabase';
import { fetchMergedCourses } from './courseSyncService';

// Database fetchers
const fetchProfileFromDB = async (): Promise<any> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .maybeSingle();

  if (error) {
    throw new Error(`Error fetching profile: ${error.message}`);
  }

  return data;
};

const fetchAchievementsFromDB = async (profileId?: string): Promise<any[]> => {
  let query = supabase
    .from('achievements')
    .select('*')
    .order('achievement_date', { ascending: false });

  if (profileId) {
    query = query.eq('profile_id', profileId);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('Error fetching achievements:', error.message);
    return [];
  }

  return data || [];
};

const fetchEducationFromDB = async (): Promise<any[]> => {
  const { data, error } = await supabase
    .from('education')
    .select('*')
    .order('start_year', { ascending: false });

  if (error) {
    console.warn('Error fetching education:', error.message);
    return [];
  }

  return data || [];
};

const fetchCertificatesFromDB = async (): Promise<Certificate[]> => {
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .order('issue_date', { ascending: false });

  if (error) {
    console.warn('Error fetching certificates:', error.message);
    return [];
  }

  return data || [];
};

const fetchGalleryFromDB = async (profileId?: string): Promise<any[]> => {
  let query = supabase
    .from('gallery')
    .select('*')
    .order('created_at', { ascending: false });

  if (profileId) {
    query = query.eq('profile_id', profileId);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('Error fetching gallery:', error.message);
    return [];
  }

  return data || [];
};

const fetchCoursesFromDB = async (): Promise<any[]> => {
  try {
    return await fetchMergedCourses();
  } catch (error) {
    console.error('Error in fetchCoursesFromDB:', error);
    return [];
  }
};

// Build profile data from database
const buildProfileData = async (): Promise<ProfileData> => {
  const profile = await fetchProfileFromDB();
  
  if (!profile) {
    throw new Error('No profile data found');
  }

  const [achievements, education, certificates, gallery, courses] = await Promise.all([
    fetchAchievementsFromDB(profile.id),
    fetchEducationFromDB(),
    fetchCertificatesFromDB(),
    fetchGalleryFromDB(profile.id),
    fetchCoursesFromDB()
  ]);

  return {
    fullName: profile.name,
    bio: profile.bio,
    profileImageUrl: profile.profile_image_url,
    landingImageUrl: profile.profile_image_url,
    achievements: achievements.map(achievement => ({
      id: achievement.id,
      title: achievement.title,
      description: achievement.description || '',
      achievement_date: achievement.achievement_date,
      category: achievement.category || 'Akademik',
      level: achievement.level || 'Tingkat 1',
      image_url: achievement.image_url,
      rank: achievement.rank || '1st',
      year: new Date(achievement.achievement_date).getFullYear(),
      date: achievement.achievement_date
    })),
    educationHistory: education.map(edu => ({
      id: edu.id,
      school: edu.school,
      level: edu.level,
      startYear: edu.start_year,
      endYear: edu.end_year,
      description: edu.description || '',
      image_url: edu.image_url,
      is_current: edu.is_current
    })),
    certificateHistory: certificates.map(cert => ({
       id: cert.id,
       certificate_name: cert.certificate_name,
       issuing_institution: cert.issuing_institution,
       issue_date: cert.issue_date,
       expiry_date: cert.expiry_date || '',
       certificate_number: cert.certificate_number,
       category: cert.category,
       description: cert.description,
       status: cert.status as 'active' | 'expired' | 'pending',
       image_url: cert.image_url
     })),
    gallery: gallery.map(item => ({
      id: item.id,
      image_url: item.image_url,
      title: item.title
    })),
    courseHistory: courses.map(course => ({
       id: course.id,
       course_name: course.course_name,
       institution: course.institution,
       category: course.category || 'Akademik',
       start_date: course.start_date,
       end_date: course.end_date,
       description: course.description || '',
       status: course.status,
       certificate_url: course.certificate_url,
       image_url: course.image_url
     }))
  };
};

// Build individual data arrays from database
const buildAchievements = async (profileId?: string) => {
  const achievements = await fetchAchievementsFromDB(profileId);
  
  return achievements.map(achievement => ({
    id: achievement.id,
    title: achievement.title,
    description: achievement.description || '',
    achievement_date: achievement.achievement_date,
    category: achievement.category || 'Akademik',
    level: achievement.level || 'Tingkat 1',
    image_url: achievement.image_url,
    rank: achievement.rank || '1st',
    year: new Date(achievement.achievement_date).getFullYear(),
    date: achievement.achievement_date
  }));
};

const buildEducation = async () => {
  const education = await fetchEducationFromDB();
  
  return education.map(edu => ({
    id: edu.id,
    school: edu.school,
    level: edu.level,
    startYear: edu.start_year,
    endYear: edu.end_year,
    description: edu.description || '',
    image_url: edu.image_url,
    is_current: edu.is_current
  }));
};

const buildCertificates = async () => {
  const certificates = await fetchCertificatesFromDB();
  
  return certificates.map(cert => ({
    id: cert.id,
    certificate_name: cert.certificate_name,
    issuing_institution: cert.issuing_institution,
    issue_date: cert.issue_date,
    expiry_date: cert.expiry_date || null,
    certificate_number: cert.certificate_number,
    category: cert.category,
    description: cert.description,
    status: cert.status as 'active' | 'expired' | 'pending',
    image_url: cert.image_url
  }));
};

const buildGallery = async (profileId?: string) => {
  const gallery = await fetchGalleryFromDB(profileId);
  
  return gallery.map(item => ({
    id: item.id,
    image_url: item.image_url,
    title: item.title
  }));
};

// Optimized functions using database
export const getOptimizedProfileData = async (): Promise<ProfileData> => {
  return await buildProfileData();
};

export const getOptimizedAchievements = async (profileId?: string) => {
  return await buildAchievements(profileId);
};

export const getOptimizedGallery = async (profileId?: string) => {
  return await buildGallery(profileId);
};

export const getOptimizedEducationHistory = async () => {
  return await buildEducation();
};

export const getOptimizedCertificateHistory = async () => {
  return await buildCertificates();
};

// React hooks for optimized data fetching
export const useOptimizedProfileDataService = (profileId?: string) => {
  return useCachedData(
    `profile-data-${profileId || 'default'}`,
    () => getOptimizedProfileData(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false
    }
  );
};

export const useOptimizedAchievementsService = (profileId?: string) => {
  return useCachedData(
    `achievements-${profileId || 'default'}`,
    () => getOptimizedAchievements(profileId),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false
    }
  );
};

export const useOptimizedGalleryService = (profileId?: string) => {
  return useCachedData(
    `gallery-${profileId || 'default'}`,
    () => getOptimizedGallery(profileId),
    {
      staleTime: 15 * 60 * 1000, // 15 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false
    }
  );
};

export const useOptimizedEducationService = () => {
  return useCachedData(
    'json-education',
    getOptimizedEducationHistory,
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 2 * 60 * 60 * 1000, // 2 hours
      refetchOnWindowFocus: false
    }
  );
};

export const useOptimizedCertificatesService = () => {
  return useCachedData(
    'json-certificates',
    getOptimizedCertificateHistory,
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 2 * 60 * 60 * 1000, // 2 hours
      refetchOnWindowFocus: false
    }
  );
};

// Function to clear cache when data is updated
export const clearOptimizedDataCache = () => {
  // Cache clearing is now handled by the useCachedData hook
  // This function is kept for backward compatibility
};

// Function to preload all data
export const preloadOptimizedData = async () => {
  try {
    await Promise.all([
      getOptimizedProfileData(),
      getOptimizedAchievements(),
      getOptimizedGallery(),
      getOptimizedEducationHistory(),
      getOptimizedCertificateHistory()
    ]);
    console.log('Optimized data preloaded successfully');
  } catch (error) {
    console.error('Error preloading optimized data:', error);
  }
};

// Backward compatibility exports
export const getProfileData = getOptimizedProfileData;
export const getAchievements = getOptimizedAchievements;
export const getGallery = getOptimizedGallery;
export const getEducationHistory = getOptimizedEducationHistory;
export const getCertificateHistory = getOptimizedCertificateHistory;